import * as React from "react";
import { notification, Modal, Button, Input, Checkbox } from "antd";
import { homeLink, linksOf, nameOfKind, parentLink, request, resourceLink } from "../../services/cpln";
import { clearItem, updateLastDeploymentTimeOnHubspot } from "../../services/utils";
import { useGVCEmitter } from "../../emitters/gvc";
import { ExportWithItemsColumn } from "./exportWithItemsModal/column";
import { LoaderSmall } from "../../components/layout/loader/small";
import { Workload } from "../../schema/types/workload/entity";
import { Identity } from "../../schema/types/identity/entity";
import { VolumeSet } from "../../schema/types/volumeSet/entity";
import { IpSet, IpSetSpec } from "../../schema/types/ipset/entity";
import { parseLink } from "../../utils/linkParser/linkParser";
import { InfoTooltip } from "../../components/InfoTooltip";
import { Gvc } from "../../schema/types/gvc/entity";
import { useAppSelector } from "../../store/hooks";
import { selectContext } from "../../store/contextSlice";

interface Props {
  gvc: Gvc;
  onClose: () => void;
  clone: (item: any) => Promise<void>;
  changeGVCContextAfterClone: (gvcName: string) => Promise<void>;
}

enum ActiveModal {
  CLONE,
  SWITCH_TO_GVC,
}

export const GVCCloneModal: React.FC<Props> = ({ gvc, clone, onClose, changeGVCContextAfterClone }) => {
  const context = useAppSelector(selectContext);
  const sourceCtx = { org: context.org, gvc: gvc.name };

  const gvcEmitter = useGVCEmitter();
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  const [cloneWithLocations, setCloneWithLocations] = React.useState(false);
  const [cloneIpset, setCloneIpset] = React.useState(false);

  const [activeModal, setActiveModal] = React.useState<ActiveModal>(ActiveModal.CLONE);

  const [workloads, setWorkloads] = React.useState<Workload[]>([]);
  const [identities, setIdentities] = React.useState<Identity[]>([]);
  const [volumesets, setVolumesets] = React.useState<VolumeSet[]>([]);

  const [workloadsFetched, setWorkloadsFetched] = React.useState<boolean>(false);
  const [identitiesFetched, setIdentitiesFetched] = React.useState<boolean>(false);
  const [volumesetsFetched, setVolumesetsFetched] = React.useState<boolean>(false);

  const [selectedWorkloadLinks, setSelectedWorkloadLinks] = React.useState<string[]>([]);
  const [selectedIdentityLinks, setSelectedIdentityLinks] = React.useState<string[]>([]);
  const [selectedVolumesetLinks, setSelectedVolumesetLinks] = React.useState<string[]>([]);

  const [requiredIdentityLinks, setRequiredIdentityLinks] = React.useState<string[]>([]);
  const [requiredVolumesetLinks, setRequiredVolumesetLinks] = React.useState<string[]>([]);

  const selectedAndRequiredIdentityLinks = [...selectedIdentityLinks];
  for (let requiredIdentityLink of requiredIdentityLinks) {
    if (!selectedAndRequiredIdentityLinks.includes(requiredIdentityLink)) {
      selectedAndRequiredIdentityLinks.push(requiredIdentityLink);
    }
  }

  const selectedAndRequiredVolumesetLinks = [...selectedVolumesetLinks];
  for (let requiredVolumesetLink of requiredVolumesetLinks) {
    if (!selectedAndRequiredVolumesetLinks.includes(requiredVolumesetLink)) {
      selectedAndRequiredVolumesetLinks.push(requiredVolumesetLink);
    }
  }

  React.useEffect(() => {
    Promise.all([getAllWorkloads(), getAllIdentities(), getAllVolumesets()]);
  }, []);

  React.useEffect(() => {
    const _requiredIdentityLinks: string[] = [];
    for (let workloadLink of selectedWorkloadLinks) {
      const workload = workloads.find((w) => linksOf(w).self === workloadLink);
      if (!workload) {
        continue;
      }
      if (workload.spec.identityLink && identities.some((i) => linksOf(i).self! === workload.spec.identityLink)) {
        _requiredIdentityLinks.push(workload.spec.identityLink);
      }
    }

    setRequiredIdentityLinks(_requiredIdentityLinks);
  }, [selectedWorkloadLinks]);

  React.useEffect(() => {
    const _requiredVolumesetLinks: string[] = [];
    for (let workloadLink of selectedWorkloadLinks) {
      const workload = workloads.find((w) => linksOf(w).self === workloadLink);
      if (!workload) {
        continue;
      }
      for (const container of workload.spec.containers) {
        for (const volume of container.volumes || []) {
          if (volume.uri.includes("cpln://volumeset/")) {
            let volumeName = "";
            try {
              volumeName = volume.uri.split("cpln://volumeset/")[1];
            } catch (e) {}
            if (volumeName) {
              _requiredVolumesetLinks.push(resourceLink("volumeset", volumeName));
            }
          }
        }
      }
    }

    setRequiredVolumesetLinks(_requiredVolumesetLinks);
  }, [selectedWorkloadLinks]);

  function toggleWorkloadAll() {
    if (selectedWorkloadLinks.length !== workloads.length) {
      setSelectedWorkloadLinks(workloads.map((w) => linksOf(w).self!));
    } else {
      setSelectedWorkloadLinks([]);
    }
  }

  function toggleWorkloadLink(link: string) {
    const isChecked = selectedWorkloadLinks.includes(link);
    if (isChecked) {
      setSelectedWorkloadLinks((links) => links.filter((_link) => _link !== link));
    } else {
      setSelectedWorkloadLinks((links) => [...links, link]);
    }
  }

  function toggleIdentityAll() {
    if (selectedAndRequiredIdentityLinks.length !== identities.length) {
      setSelectedIdentityLinks(identities.map((i) => linksOf(i).self!));
    } else {
      setSelectedIdentityLinks([...requiredIdentityLinks]);
    }
  }

  function toggleIdentityLink(link: string) {
    const isChecked = selectedIdentityLinks.includes(link);
    if (isChecked) {
      setSelectedIdentityLinks((links) => links.filter((_link) => _link !== link));
    } else {
      setSelectedIdentityLinks((links) => [...links, link]);
    }
  }

  function toggleVolumesetAll() {
    if (selectedAndRequiredVolumesetLinks.length !== volumesets.length) {
      setSelectedVolumesetLinks(
        volumesets.map((w) => {
          const selfLink = linksOf(w).self!;
          return selfLink;
        }),
      );
    } else {
      setSelectedVolumesetLinks([...requiredVolumesetLinks]);
    }
  }

  function toggleVolumesetLink(link: string) {
    const isChecked = selectedVolumesetLinks.includes(link);
    if (isChecked) {
      setSelectedVolumesetLinks((links) => links.filter((_link) => _link !== link));
    } else {
      setSelectedVolumesetLinks((links) => [...links, link]);
    }
  }

  async function getAllWorkloads() {
    let nextLink = parentLink("workload", sourceCtx);
    const _workloads: Workload[] = [];
    while (nextLink) {
      const { data: workloadsRes } = await request({ url: nextLink });
      for (let item of workloadsRes.items) {
        _workloads.push(item);
      }
      nextLink = workloadsRes.links.find((link: any) => link.rel === "next")?.href;
    }
    setWorkloads(_workloads);
    setWorkloadsFetched(true);
  }

  async function getAllIdentities() {
    let nextLink = parentLink("identity", sourceCtx);
    const _identities: Identity[] = [];
    while (nextLink) {
      const { data: identitiesRes } = await request({ url: nextLink });
      for (let item of identitiesRes.items) {
        _identities.push(item);
      }
      nextLink = identitiesRes.links.find((link: any) => link.rel === "next")?.href;
    }
    setIdentities(_identities);
    setIdentitiesFetched(true);
  }

  async function getAllVolumesets() {
    let nextLink = parentLink("volumeset", sourceCtx);
    const _volumesets: VolumeSet[] = [];
    while (nextLink) {
      const { data: volumesetsRes } = await request({ url: nextLink });
      for (let item of volumesetsRes.items) {
        _volumesets.push(item);
      }
      nextLink = volumesetsRes.links.find((link: any) => link.rel === "next")?.href;
    }
    setVolumesets(_volumesets);
    setVolumesetsFetched(true);
  }

  async function getIpsets(ipsetLinks: string[]) {
    const _ipsets: IpSet[] = [];
    for (const ipsetLink of ipsetLinks) {
      try {
        const { data } = await request({ url: ipsetLink });
        _ipsets.push(data);
      } catch (e) {
        // remain silent
      }
    }
    return _ipsets;
  }

  async function confirmClone() {
    try {
      // TODO check validity here
      if (!name) return;
      setIsLoading(true);

      let { data: gvcToClone } = await request<Gvc>({ url: linksOf(gvc).self! });

      // override description
      if (description) {
        gvcToClone.description = description;
      } else {
        gvcToClone.description = name;
      }

      // override name
      gvcToClone.name = name;

      // @ts-ignore
      delete gvcToClone.spec.domain;
      // @ts-ignore
      delete gvcToClone.spec?.tracing;

      if (!cloneWithLocations) {
        gvcToClone.spec!.staticPlacement = { locationLinks: [] };
      }
      if (!cloneIpset) {
        delete gvcToClone.spec?.loadBalancer?.ipSet;
      } else if (gvcToClone.spec?.loadBalancer?.dedicated && !!gvcToClone.spec?.loadBalancer?.ipSet) {
        const ipsetLink = gvcToClone.spec!.loadBalancer!.ipSet;
        const { data: ipsetItem } = await request<IpSet>({ url: ipsetLink });
        const newIpsetSpec: Partial<IpSetSpec> = {
          link: resourceLink("gvc", gvcToClone.name),
          locations: ipsetItem.spec?.locations || [],
        };
        const newIpsetItem: Partial<IpSet> = {
          name: `${gvcToClone.name}-${ipsetItem.name}`,
          description:
            ipsetItem.description === ipsetItem.name
              ? `${ipsetItem.description} for ${gvcToClone.name}`
              : ipsetItem.description,
          kind: "ipset",
          tags: ipsetItem.tags || {},
          spec: newIpsetSpec,
        };
        await request<IpSet>({ url: homeLink("ipset"), body: newIpsetItem, method: "post" });
        gvcToClone.spec!.loadBalancer!.ipSet = resourceLink("ipset", newIpsetItem.name);
      }

      await clone(gvcToClone);

      const gvcName = gvcToClone.name;

      const succeededWorkloads: { name: string; reason: string }[] = [];
      const succeededIdentities: { name: string; reason: string }[] = [];
      const succeededVolumesets: { name: string; reason: string }[] = [];
      const succeededIpSets: { name: string; reason: string }[] = [];

      const failedWorkloads: { name: string; reason: string }[] = [];
      const failedIdentities: { name: string; reason: string }[] = [];
      const failedVolumesets: { name: string; reason: string }[] = [];
      const failedIpSets: { name: string; reason: string }[] = [];

      for (let identityLink of selectedAndRequiredIdentityLinks) {
        const identity = identities.find((i) => linksOf(i).self! === identityLink);
        if (!identity) {
          continue;
        }
        const newIdentityLink = `/org/${context.org}/gvc/${gvcName}/identity`;
        try {
          const body = JSON.parse(JSON.stringify(identity));

          // Make slim
          clearItem(body);

          await request({ url: newIdentityLink, body: identity, method: "post" });
          succeededIdentities.push({ name: identity.name, reason: "" });
        } catch (e) {
          let errorMessage = e?.response?.data?.message;
          if (!errorMessage) {
            errorMessage = e.message;
          }
          failedIdentities.push({ name: identity.name, reason: errorMessage });
        }
      }

      for (let volumesetLink of selectedAndRequiredVolumesetLinks) {
        const volumeset = volumesets.find((i) => linksOf(i).self! === volumesetLink);
        if (!volumeset) {
          continue;
        }
        const newVolumesetLink = `/org/${context.org}/gvc/${gvcName}/volumeset`;
        try {
          const body = JSON.parse(JSON.stringify(volumeset));

          // Make slim
          clearItem(body);

          await request({ url: newVolumesetLink, body, method: "post" });
          succeededVolumesets.push({ name: volumeset.name, reason: "" });
        } catch (e) {
          let errorMessage = e?.response?.data?.message;
          if (!errorMessage) {
            errorMessage = e.message;
          }
          failedVolumesets.push({ name: volumeset.name, reason: errorMessage });
        }
      }

      let requiredIpsetLinks: string[] = [];
      for (let workloadLink of selectedWorkloadLinks) {
        const workload = workloads.find((i) => linksOf(i).self! === workloadLink);
        if (!workload) {
          continue;
        }
        const workloadIpsetLink = workload.spec?.loadBalancer?.direct?.ipSet;
        if (workloadIpsetLink) {
          requiredIpsetLinks.push(workloadIpsetLink);
        }
      }
      const ipsets = await getIpsets(requiredIpsetLinks);
      const ipsetsToClone: IpSet[] = [];

      // iterate workloads, if has ipset link, generate ipset name and update the ipset link with the new name
      // clone the ipset with new name, if it had a workload link, update the gvc part of it

      for (let workloadLink of selectedWorkloadLinks) {
        const workload = workloads.find((w) => linksOf(w).self === workloadLink);
        if (!workload) {
          continue;
        }
        const newWorkloadParentLink = `/org/${context.org}/gvc/${gvcName}/workload`;
        try {
          const body: Workload = JSON.parse(JSON.stringify(workload));
          if (!body.spec) {
            body.spec = {} as any;
          }
          if (body.spec!.identityLink) {
            body.spec!.identityLink = body.spec!.identityLink.replace(`/gvc/${gvc.name}/`, `/gvc/${gvcName}/`);
          }

          // Handle ipset and its reference in workload naming
          if (body.spec!.loadBalancer?.direct?.ipSet) {
            const ipsetItem = ipsets.find((i) => i.spec?.link === workloadLink);
            if (ipsetItem) {
              const ipsetItemCloned: IpSet = JSON.parse(JSON.stringify(ipsetItem))!;
              const ipsetLink = body.spec!.loadBalancer?.direct?.ipSet;
              const { name: ipsetName } = parseLink(ipsetLink, { useInputCtx: true });
              const newIpsetName = `${ipsetName}-${gvcName}`;
              const newIpsetLink = ipsetLink.replace(ipsetName, newIpsetName);
              body.spec!.loadBalancer.direct.ipSet = newIpsetLink;
              ipsetItemCloned.name = newIpsetName;
              const newWorkloadLink = `${newWorkloadParentLink}/${workload.name}`;
              if (!ipsetItemCloned.spec) {
                ipsetItemCloned.spec = {};
              }
              ipsetItemCloned.spec.link = newWorkloadLink;
              ipsetsToClone.push(ipsetItemCloned);
            }
          }

          // Make slim
          clearItem(body);

          await request({ url: newWorkloadParentLink, body, method: "post" });
          succeededWorkloads.push({ name: workload.name, reason: "" });
        } catch (e) {
          let errorMessage = e?.response?.data?.message;
          if (!errorMessage) {
            errorMessage = e.message;
          }
          failedWorkloads.push({ name: workload.name, reason: errorMessage });
        }
      }

      for (let ipsetToClone of ipsetsToClone) {
        try {
          // Make slim
          clearItem(ipsetToClone);

          await request({ url: homeLink("ipset"), body: ipsetToClone, method: "post" });
          succeededIpSets.push({ name: ipsetToClone.name, reason: "" });
        } catch (e) {
          let errorMessage = e?.response?.data?.message;
          if (!errorMessage) {
            errorMessage = e.message;
          }
          failedIpSets.push({ name: ipsetToClone.name, reason: errorMessage });
        }
      }

      notification.success({
        message: "Success",
        description: `Cloned ${nameOfKind(gvc.kind)}`,
      });
      if (succeededWorkloads.length > 0) {
        notification.success({
          message: "Succeeded Workloads",
          description: (
            <ul>
              {succeededWorkloads.map(({ name }) => (
                <li key={name} className="my-2">
                  <span className="block">{name}</span>
                </li>
              ))}
            </ul>
          ),
        });
        updateLastDeploymentTimeOnHubspot();
      }
      if (succeededIdentities.length > 0) {
        notification.success({
          message: "Succeeded Identities",
          description: (
            <ul>
              {succeededIdentities.map(({ name }) => (
                <li key={name} className="my-2">
                  <span className="block">{name}</span>
                </li>
              ))}
            </ul>
          ),
        });
      }
      if (succeededVolumesets.length > 0) {
        notification.success({
          message: "Succeeded Volume Sets",
          description: (
            <ul>
              {succeededVolumesets.map(({ name }) => (
                <li key={name} className="my-2">
                  <span className="block">{name}</span>
                </li>
              ))}
            </ul>
          ),
        });
      }
      if (failedWorkloads.length > 0) {
        notification.warning({
          duration: 0,
          message: "Failed Workloads",
          description: (
            <ul>
              {failedWorkloads.map(({ name, reason }) => (
                <li key={name} className="my-2">
                  <span className="block">{name}</span>
                  <span className="block">{reason}</span>
                </li>
              ))}
            </ul>
          ),
        });
      }
      if (failedIdentities.length > 0) {
        notification.warning({
          duration: 0,
          message: "Failed Identities",
          description: (
            <ul>
              {failedIdentities.map(({ name, reason }) => (
                <li key={name} className="my-2">
                  <span className="block">{name}</span>
                  <span className="block">{reason}</span>
                </li>
              ))}
            </ul>
          ),
        });
      }
      if (failedVolumesets.length > 0) {
        notification.warning({
          duration: 0,
          message: "Failed Volume Sets",
          description: (
            <ul>
              {failedVolumesets.map(({ name, reason }) => (
                <li key={name} className="my-2">
                  <span className="block">{name}</span>
                  <span className="block">{reason}</span>
                </li>
              ))}
            </ul>
          ),
        });
      }

      setIsLoading(false);
      setActiveModal(ActiveModal.SWITCH_TO_GVC);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
      setIsLoading(false);
    }
  }

  return (
    <>
      {activeModal == ActiveModal.CLONE ? (
        <Modal
          title={"Clone GVC"}
          open={true}
          maskClosable={false}
          destroyOnHidden
          width={900}
          onCancel={onClose}
          onOk={confirmClone}
          cancelButtonProps={{ disabled: isLoading }}
          okButtonProps={{
            disabled: isLoading || !name || !workloadsFetched || !identitiesFetched || !volumesetsFetched,
            loading: isLoading || !workloadsFetched || !identitiesFetched || !volumesetsFetched,
          }}
        >
          {workloadsFetched && identitiesFetched && volumesetsFetched ? (
            <div className="flex flex-col">
              <div className="mb-1">
                <div>Name</div>
                <Input name="name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="mb-1">
                <div>Description</div>
                <Input name="description" value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
              <div className="flex items-center mb-1">
                <Checkbox checked={cloneWithLocations} onChange={(e) => setCloneWithLocations(e.target.checked)}>
                  Clone with Same GVC Locations
                </Checkbox>
                <InfoTooltip
                  title={[
                    "When checked, cloned gvc uses the same locations.",
                    "When not checked, gvc is cloned with an empty location list.",
                  ]}
                />
              </div>
              {gvc.spec.loadBalancer?.dedicated && gvc.spec.loadBalancer.ipSet ? (
                <div className="flex items-center mb-1">
                  <Checkbox checked={cloneIpset} onChange={(e) => setCloneIpset(e.target.checked)}>
                    Clone linked IP Set
                  </Checkbox>
                  <InfoTooltip
                    title={[
                      "When checked, ip set is cloned with the same spec and assigned to cloned gvc.",
                      "When not checked, gvc is cloned without ip set.",
                    ]}
                  />
                </div>
              ) : null}
              <div style={{ margin: "0.25rem 0" }}>Select GVC items to export.</div>
              <div className="export-with-items-modal-columns">
                <ExportWithItemsColumn
                  items={workloads.map((workload) => ({
                    value: workload.name,
                    checked: selectedWorkloadLinks.includes(linksOf(workload).self!),
                    onChange: () => toggleWorkloadLink(linksOf(workload).self!),
                  }))}
                  kind="workload"
                  selectedItemsLength={selectedWorkloadLinks.length}
                  toggleAllItems={toggleWorkloadAll}
                />
                <ExportWithItemsColumn
                  items={identities.map((identity) => {
                    const disabled = requiredIdentityLinks.includes(linksOf(identity).self!);
                    const onChange = disabled ? undefined : () => toggleIdentityLink(linksOf(identity).self!);
                    return {
                      value: identity.name,
                      disabled: disabled,
                      checked: selectedAndRequiredIdentityLinks.includes(linksOf(identity).self!),
                      onChange: onChange,
                    };
                  })}
                  kind="identity"
                  selectedItemsLength={selectedAndRequiredIdentityLinks.length}
                  toggleAllItems={toggleIdentityAll}
                />
                <ExportWithItemsColumn
                  items={volumesets.map((volumeset) => {
                    const selfLink = linksOf(volumeset).self!;
                    const disabled = requiredVolumesetLinks.includes(selfLink);
                    const onChange = disabled ? undefined : () => toggleVolumesetLink(selfLink);
                    return {
                      value: volumeset.name,
                      disabled: disabled,
                      checked: selectedAndRequiredVolumesetLinks.includes(selfLink),
                      onChange: onChange,
                    };
                  })}
                  kind="volumeset"
                  selectedItemsLength={selectedAndRequiredVolumesetLinks.length}
                  toggleAllItems={toggleVolumesetAll}
                />
              </div>
            </div>
          ) : (
            <div
              style={{
                width: "100%",
                height: "360px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LoaderSmall />
            </div>
          )}
        </Modal>
      ) : null}
      {activeModal == ActiveModal.SWITCH_TO_GVC ? (
        <Modal
          title="Do you want to change context?"
          open={true}
          maskClosable={false}
          destroyOnHidden
          onCancel={() => {
            onClose();
            gvcEmitter.emit("GVC_CLONED");
          }}
          onOk={async () => {
            setIsLoading(true);
            try {
              await changeGVCContextAfterClone(name);
            } catch (e) {}
            setIsLoading(false);
            onClose();
          }}
          cancelButtonProps={{ disabled: isLoading }}
          okButtonProps={{ disabled: isLoading, loading: isLoading }}
        >
          This will change your current context and set it to newly cloned GVC item.
        </Modal>
      ) : null}
    </>
  );
};
